import type {
  CasesBreakdownEntry,
  GetCasesAnalyticsResponse,
} from '@compass/client/types/cases_analytics';

import { CHART_COLOR_VARS, COLOR_MUTED } from '@/pages/analytics/utils/chartPalette';

type Grouped = { group?: string }[] | undefined;

type Dimension = 'by_report_type' | 'by_program' | 'by_submission_method';

/** Every list of the response that carries group keys, under the dimension it groups by. */
const listsByDimension = (response: GetCasesAnalyticsResponse): Record<Dimension, Grouped[]> => ({
  by_report_type: [
    response.submitted_trend?.by_report_type,
    response.time_to_submit?.by_report_type,
    response.breakdown?.by_report_type,
  ],
  by_program: [
    response.submitted_trend?.by_program,
    response.time_to_submit?.by_program,
    response.breakdown?.by_program,
    // One line per program under the SLA compliance chart's By Program scope.
    response.sla_compliance_trend,
  ],
  by_submission_method: [
    response.submitted_trend?.by_submission_method,
    response.time_to_submit?.by_submission_method,
    response.breakdown?.by_submission_method,
  ],
});

/**
 * Orders a dimension's group keys largest first, the same way the charts fold them: by case
 * count (from the "Case breakdown" donut), ties by display name then key. The theme colours then
 * land on exactly the slices the donut keeps, and the folded remainder matches its grey "Other".
 */
const rankBySize = (keys: Set<string>, sizes: CasesBreakdownEntry[] | undefined): string[] => {
  const count = new Map<string, number>();
  const name = new Map<string, string>();
  for (const entry of sizes ?? []) {
    if (!entry.group) continue;
    count.set(entry.group, entry.case_count ?? 0);
    if (entry.group_name) name.set(entry.group, entry.group_name);
  }
  return [...keys].sort(
    (a, b) =>
      (count.get(b) ?? 0) - (count.get(a) ?? 0) ||
      (name.get(a) ?? a).localeCompare(name.get(b) ?? b) ||
      a.localeCompare(b),
  );
};

/**
 * One colour per group key (a program, report type or submission method) across every chart of
 * the page, so a program keeps its colour in the trend, the donut and the histogram.
 *
 * A dimension with no more keys than there are theme colours gets a stable, name-ordered run of
 * them, so the colour does not depend on which chart or filter lists a key first and the page
 * reads like QC Analytics (every categorical series a `--chart-N` token). A dimension with more
 * keys than that — in practice By Program — gives the theme colours to its largest groups, in the
 * donut's own fold order, and folds the rest into the muted "Other" the charts show past the top
 * five.
 */
export const buildGroupColors = (
  response: GetCasesAnalyticsResponse | undefined,
): Map<string, string> => {
  const colors = new Map<string, string>();
  if (!response) return colors;

  const byDimension = listsByDimension(response);
  for (const dimension of Object.keys(byDimension) as Dimension[]) {
    const keys = new Set<string>();
    for (const list of byDimension[dimension]) {
      for (const row of list ?? []) if (row.group) keys.add(row.group);
    }
    keys.delete('overall');

    if (keys.size <= CHART_COLOR_VARS.length) {
      [...keys].sort().forEach((key, index) => colors.set(key, CHART_COLOR_VARS[index]));
      continue;
    }

    rankBySize(keys, response.breakdown?.[dimension]).forEach((key, index) =>
      colors.set(key, index < CHART_COLOR_VARS.length ? CHART_COLOR_VARS[index] : COLOR_MUTED),
    );
  }
  return colors;
};
